// src/components/ClassSchedulerPage.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { parseTime, formatTimeFromMinutes, getWeekNumber } from '../utils/helpers.ts';
import { idStartsWith } from '../utils/typeHelpers.ts'; // Import type-safe helper
import logger from '../utils/logger';
import DayTableSimple from './DayTableSimple.tsx';
import Button from './ui/Button';
import { useScheduleDragAndDrop, KEY_SEPARATOR } from '../hooks/useScheduleDragAndDrop'; // Import the custom hook
import { useActiveDays } from '../hooks/useActiveDays';
import { useDragToCreate } from '../hooks/useDragToCreate'; // Import the new drag-to-create hook
import { exportScheduleEntries } from '../services/apiService';
import UnscheduledEntryContainer from './UnscheduledEntryContainer'; // Import the new component
import EventContainer from './EventContainer'; // Import the event container component
import SidebarDrawerToggle from './SidebarDrawerToggle'; // Import the toggle component
import ScheduleEntry from '../models/ScheduleEntry.ts'; // Add this import
import { parseWeekSeries, getWeekDateRange, doDateRangesOverlap } from '../utils/timeUtils'; // ADDED: Time utils
import { deriveScheduleTimelineContext } from '../utils/scheduleContext';
import EditScheduleItemModal from './EditScheduleItemModal'; // Import the modal component
import LiveBackupManager from '../services/LiveBackupManager.js';
// Room view switched to RoomsOccupancyView
import RoomsOccupancyView from './RoomsOccupancyView.jsx';
import TeacherView from './TeacherView'; // Import the new teacher view component
// ChangeLogModal moved into FloatingActionButtons
// ReviewChangesButton now rendered inside header
import ConflictListModal from './ConflictListModal'; // Import the conflict list modal
import { conflictManager } from '../services/ConflictManager.ts'; // Import the conflict manager
import DebugDrawer from './ClassSchedulerPage/DebugDrawer';
import ClassSchedulerHeader from './ClassSchedulerPage/ClassSchedulerHeader';
import { useClassSchedulerState } from './ClassSchedulerPage/useClassSchedulerState';
import { SharedScrollProvider } from '../contexts/SharedScrollContext';
import FloatingActionButtons from './ClassSchedulerPage/FloatingActionButtons.tsx';
// Icons moved to SidebarDrawerToggle component

// --- Constants for DnD ---
const ItemTypes = {
    COURSE: 'course', // Used by DraggableCourseItem
    SCHEDULED_ITEM: 'scheduled_item' // Could be used by items on DayTableSimple if needed
};



// DebugDrawer moved to ./ClassSchedulerPage/DebugDrawer


// --- Main ClassSchedulerPage Component ---
const ClassSchedulerPage = ({ user }) => {
    // --- Hook: encapsulated state & data fetching ---
    const {
        // Services
        assetManager,
        draftManager,

        // State
        isLiveMode,
        currentDraftName,
        courseDefinitions,
        isFetchingDefinitions,
        hasLoadedDefinitionsOnce,
        fetchDefinitionsError,
        teachers,
        groups,
        subjects,
        rooms,
        isLoadingAssets,
        assetsError,
        timePeriods,
        selectedPeriodId,
        setSelectedPeriodId,
        isFetchingPeriods,
        fetchPeriodsError,
        yearStructure,
        isFetchingYearStructure,
        scheduledInstances,
        setScheduledInstances: originalSetScheduledInstances,
        filteredScheduledInstances,
        setFilteredScheduledInstances,
        timelineViewRange,
        setTimelineViewRange,
        textFilterQuery,
        setTextFilterQuery,
        currentView,
        setCurrentView,
        selectedRoomForView,
        setSelectedRoomForView,
        selectedTeacherForView,
        setSelectedTeacherForView,
        selectedYearForView,
        setSelectedYearForView,
        selectedMonth,
        setSelectedMonth,
        selectedWeek,
        setSelectedWeek,
        setDraftRefreshTrigger,
        isDraftManagerInitialized,
        isBaselineSet,
        setIsBaselineSet,
        hasInitialConflictsCalculated,
        setHasInitialConflictsCalculated,
        isLoading,
        setIsLoading,
        error,
        setError,
        success,
        setSuccess,
        isImporting,
        setIsImporting,
        importError,
        setImportError,
        importSuccess,
        setImportSuccess,
        isExporting,
        setIsExporting,
        isDebugDrawerOpen,
        setIsDebugDrawerOpen,

        // Computed
        availableYears,

        // Handlers
        handleModeChange,
        handleDraftChange,
        toggleDebugDrawer
        ,
        // Save/change tracking and baseline passthrough
        changeLog,
        addLogEntry,
        calculateNetChanges,
        isChangeLogModalOpen,
        openChangeLogModal,
        closeChangeLogModal,
        handleSaveChanges,
        serverBaseline,
        saveChangesToAPI,
        handleSaveSchedule,
        getChangeIdCounts,
        hasPendingChangesByIdSets,
        refreshScheduleEntries,
        // Undo/Redo functionality
        handleUndo,
        handleRedo,
        canUndo,
        canRedo,
        getUndoRedoState,

        // Teacher management
        handleAddTeacher,

        // Subject management
        handleAddSubject,

        // Group management
        handleAddGroup
    } = useClassSchedulerState(user);

    // Wrapped setScheduledInstances with comprehensive logging
    const setScheduledInstances = useCallback((newValue) => {
        const stack = new Error().stack;
        const caller = stack?.split('\n')[2]?.trim() || 'unknown';

        console.log('*** SETSCHEDULEDINSTANCES CALLED ***', {
            timestamp: new Date().toISOString(),
            caller: caller,
            newValueType: typeof newValue,
            newValueLength: Array.isArray(newValue) ? newValue.length : 'not-array',
            currentLength: scheduledInstances.length,
            isFunction: typeof newValue === 'function',
            isArray: Array.isArray(newValue)
        });

        if (Array.isArray(newValue)) {
            console.log('*** SETSCHEDULEDINSTANCES - NEW ARRAY DETAILS ***', {
                length: newValue.length,
                sampleIds: newValue.slice(0, 5).map(item => item?.id),
                allIds: newValue.map(item => item?.id)
            });
        } else if (typeof newValue === 'function') {
            console.log('*** SETSCHEDULEDINSTANCES - FUNCTION UPDATE ***', {
                currentLength: scheduledInstances.length,
                currentSampleIds: scheduledInstances.slice(0, 5).map(item => item?.id)
            });
        }

        logger.debug('[ClassSchedulerPage] setScheduledInstances called', {
            caller: caller,
            newValueType: typeof newValue,
            newValueLength: Array.isArray(newValue) ? newValue.length : 'not-array',
            currentLength: scheduledInstances.length
        });

        return originalSetScheduledInstances(newValue);
    }, [originalSetScheduledInstances, scheduledInstances.length]);

    // State moved to useClassSchedulerState

    // --- Derived State & Memoized Values ---

    // Trace baseline and changes interaction on this page
    useEffect(() => {
        console.debug('[ClassSchedulerPage] useChangeTracking snapshot', {
            changeLogLength: changeLog.length,
            serverBaselineLen: Array.isArray(serverBaseline) ? serverBaseline.length : 'n/a'
        });
    }, [changeLog.length, serverBaseline]);

    // Reflect hook baseline readiness into page-level flag
    useEffect(() => {
        const ready = Array.isArray(serverBaseline) && serverBaseline.length > 0;
        if (isBaselineSet !== ready) {
            setIsBaselineSet(ready);
        }
    }, [serverBaseline, isBaselineSet, setIsBaselineSet]);

    // State provided by useClassSchedulerState

    // Debug: Log when filteredScheduledInstances changes
    useEffect(() => {

        if (filteredScheduledInstances.length > 0) {
            logger.debug('[ClassSchedulerPage] Sample filtered item:', {
                id: filteredScheduledInstances[0].id,
                course: filteredScheduledInstances[0].course,
                day: filteredScheduledInstances[0].day,
                timeStart: filteredScheduledInstances[0].timeStart,
                room: filteredScheduledInstances[0].room
            });
        }
    }, [filteredScheduledInstances]);
    // State provided by useClassSchedulerState

    // Draft initialization moved into useClassSchedulerState

    // Draft state listening handled within useClassSchedulerState

    // Cleanup on unmount - clear localStorage if needed
    useEffect(() => {
        return () => {
            // Optional: Clear draft state on unmount if in a temporary state
            // For now, we'll keep the state persistent across sessions
            logger.debug('[ClassSchedulerPage] Component unmounting - draft state preserved');
        };
    }, []);

    // Undo/Redo keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Only handle shortcuts when not typing in an input
            if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable) {
                return;
            }

            // Ctrl+Z or Cmd+Z for undo
            if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
                event.preventDefault();
                if (canUndo()) {
                    const success = handleUndo();
                    if (success) {
                        setSuccess('Undid last action');
                        setTimeout(() => setSuccess(null), 2000);
                    }
                }
            }
            // Ctrl+Y, Ctrl+Shift+Z, or Cmd+Shift+Z for redo
            else if ((event.ctrlKey && event.key === 'y') ||
                (event.ctrlKey && event.shiftKey && event.key === 'Z') ||
                (event.metaKey && event.shiftKey && event.key === 'Z')) {
                event.preventDefault();
                if (canRedo()) {
                    const success = handleRedo();
                    if (success) {
                        setSuccess('Redid last action');
                        setTimeout(() => setSuccess(null), 2000);
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleUndo, handleRedo, canUndo, canRedo, setSuccess]);

    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [selectedScheduleItem, setSelectedScheduleItem] = useState(null);
    // Ref to hold the save-to-API function to avoid initialization order issues
    const saveChangesToAPIRef = useRef(null);
    const lastEditedItemIdRef = useRef(null);
    const updatedItemsForAutoSaveRef = useRef(null);
    const changeLogLengthRef = useRef(0);
    const isSavingDraftRef = useRef(false);
    const scheduledInstancesRef = useRef(scheduledInstances);
    useEffect(() => {
        changeLogLengthRef.current = changeLog.length;
    }, [changeLog.length]);

    // Keep ref in sync with scheduledInstances
    useEffect(() => {
        scheduledInstancesRef.current = scheduledInstances;
    }, [scheduledInstances]);

    // Conflict detection state
    const [currentConflicts, setCurrentConflicts] = useState([]);
    const [isConflictListVisible, setIsConflictListVisible] = useState(false);
    const [conflictCalculationTimeoutId, setConflictCalculationTimeoutId] = useState(null);
    const [isCalculatingConflicts, setIsCalculatingConflicts] = useState(false);

    // Save animation state
    const [isSaveAnimationActive, setIsSaveAnimationActive] = useState(false);

    // Track locally deleted items to prevent them from being re-added by filtering effects
    const [locallyDeletedItemIds, setLocallyDeletedItemIds] = useState(new Set());

    // Track newly saved item for scrolling and highlighting
    const [highlightedItemId, setHighlightedItemId] = useState(null);

    const [entryTypeFilters, setEntryTypeFilters] = useState({
        class: true,
        meeting: true,
        event: true
    });

    // Drawer state for unscheduled courses container
    const [isUnscheduledDrawerOpen, setIsUnscheduledDrawerOpen] = useState(false);

    // Drawer state for events container
    const [isEventDrawerOpen, setIsEventDrawerOpen] = useState(false);

    // Drawer state for meetings container
    const [isMeetingDrawerOpen, setIsMeetingDrawerOpen] = useState(false);

    const currentDate = new Date();

    const isAdmin = user?.role === 'Admin';

    // Scroll tracking state for fixed day header (will be initialized after activeDays is loaded)
    const [currentVisibleDay, setCurrentVisibleDay] = useState('');
    const [previousDay, setPreviousDay] = useState('');
    const scrollContainerRef = useRef(null);
    const dayHeaderRefs = useRef({});

    // Track page header hide/show based on inner scroll
    const setupHeaderHiddenRef = useRef(false);
    const lastInnerScrollTopRef = useRef(0);
    const maxDownInnerScrollTopRef = useRef(0);

    // Get unique rooms from real data instead of mock data
    const allRoomsForGrid = useMemo(() => {
        if (rooms.length === 0) return [];
        return rooms; // Pass room objects directly instead of extracting names
    }, [rooms]);

    // Track unsaved changes
    const hasUnsavedChanges = useMemo(() => Array.isArray(changeLog) && changeLog.length > 0, [changeLog.length]);

    // Warn on page unload if there are unsaved changes
    useEffect(() => {
        const beforeUnloadHandler = (e) => {
            if (!hasUnsavedChanges) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', beforeUnloadHandler);
        return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
    }, [hasUnsavedChanges]);

    // Block in-app navigation when there are unsaved changes (without data router)
    useEffect(() => {
        if (!hasUnsavedChanges) return;

        const shouldIntercept = (anchor) => {
            if (!anchor || !anchor.href) return false;
            try {
                const url = new URL(anchor.href, window.location.origin);
                return url.origin === window.location.origin && url.pathname !== window.location.pathname;
            } catch (_) { return false; }
        };

        const onClick = (e) => {
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            let el = e.target;
            while (el && el !== document.body) {
                if (el.tagName === 'A' && shouldIntercept(el)) {
                    const ok = window.confirm('You have unsaved changes. If you navigate away, they will be lost. Continue?');
                    if (!ok) e.preventDefault();
                    return;
                }
                el = el.parentElement;
            }
        };

        const onPopState = () => {
            const ok = window.confirm('You have unsaved changes. If you navigate away, they will be lost. Continue?');
            if (!ok) {
                window.history.pushState(null, '', window.location.href);
            }
        };

        document.addEventListener('click', onClick, true);
        window.addEventListener('popstate', onPopState);
        return () => {
            document.removeEventListener('click', onClick, true);
            window.removeEventListener('popstate', onPopState);
        };
    }, [hasUnsavedChanges]);

    // Assets fetching moved into useClassSchedulerState

    // Helper function to check if a week is within academic year bounds
    const isWeekWithinAcademicYear = useCallback((weekNumber, academicStartWeek, academicEndWeek) => {
        if (!academicStartWeek || !academicEndWeek) return true; // If no bounds set, include all weeks

        // Handle normal academic year (e.g., start_week=1, end_week=52)
        if (academicStartWeek <= academicEndWeek) {
            return weekNumber >= academicStartWeek && weekNumber <= academicEndWeek;
        }

        // Handle cross-year academic year (e.g., start_week=36, end_week=35)
        // This means weeks 36-53 and weeks 1-35 are valid
        return weekNumber >= academicStartWeek || weekNumber <= academicEndWeek;
    }, []);



    // Initialize the drag-to-create hook

    // Helper to set the item and open the modal with autosave behavior
    const openModalWithAutosave = useCallback((item) => {
        // Try to infer a concrete week context when possible to surface exception UI in the editor
        const determineWeekContext = () => {
            if (selectedWeek && typeof selectedWeek.week === 'number') {
                return { week: selectedWeek.week, year: selectedWeek.year };
            }
            if (timelineViewRange && timelineViewRange.startWeek && timelineViewRange.endWeek && timelineViewRange.startWeek === timelineViewRange.endWeek) {
                return { week: timelineViewRange.startWeek, year: selectedYearForView };
            }
            return null;
        };

        const maybeAttachExceptionContext = (entry) => {
            const ctx = determineWeekContext();
            if (!ctx) return entry; // No single-week context available

            try {
                const root = (scheduledInstancesRef.current || []).find(it => String(it.id) === String(entry.id));
                if (!root || typeof root.getEntryForWeek !== 'function') return entry;
                const weekEntry = root.getEntryForWeek(ctx.week, ctx.year);
                if (!weekEntry) return entry; // cancelled this week
                // If weekEntry has a different id, it's an exception occurrence
                if (weekEntry.id !== root.id) {
                    try {
                        const cloned = typeof entry.clone === 'function'
                            ? entry.clone()
                            : new ScheduleEntry(typeof entry.toObject === 'function' ? entry.toObject() : entry);
                        try { cloned._exceptionContext = { originalId: root.id, week: ctx.week, year: ctx.year }; } catch (_) { }
                        return cloned;
                    } catch (_) {
                        try { entry._exceptionContext = { originalId: root.id, week: ctx.week, year: ctx.year }; } catch (_) { }
                        return entry;
                    }
                }
                return entry;
            } catch (_) {
                return entry;
            }
        };

        const enrichedItem = maybeAttachExceptionContext(item);
        setSelectedScheduleItem(enrichedItem);
        setIsScheduleModalOpen(true);
    }, [setIsScheduleModalOpen, setSelectedScheduleItem, selectedWeek, selectedYearForView, timelineViewRange]);

    const derivedTimelineContext = useMemo(() => deriveScheduleTimelineContext({
        selectedWeek,
        timelineViewRange,
        selectedYearForView,
        selectedPeriod: selectedPeriodId ? timePeriods.find(p => String(p.id) === String(selectedPeriodId)) : null,
        fallbackDate: currentDate
    }), [selectedWeek, timelineViewRange, selectedYearForView, selectedPeriodId, timePeriods, currentDate]);

    const {
        dragToCreateData,
        handleDragToCreateStart,
        handleDragToCreateMove,
        handleDragToCreateEnd,
        handleDragToCreateCancel
    } = useDragToCreate(user?.role === 'Admin', {
        onModalCreate: true,
        setSelectedScheduleItem,
        setIsScheduleModalOpen: setIsScheduleModalOpen,
        currentYear: derivedTimelineContext.year,
        currentDate: derivedTimelineContext.date,
        rooms: rooms, // Pass rooms array for room name lookup
        timelineContext: derivedTimelineContext,
        // NEW: Provide context-specific week range for ClassSchedulerPage
        getContextWeekRange: () => {
            // Priority 1: Use timeline view range if available (user-selected range)
            if (timelineViewRange && timelineViewRange.startWeek && timelineViewRange.endWeek) {
                logger.debug('[ClassSchedulerPage] Using timeline view range for drag-to-create:',
                    timelineViewRange.startWeek, '-', timelineViewRange.endWeek);

                // Format as week series string
                if (timelineViewRange.startWeek === timelineViewRange.endWeek) {
                    return timelineViewRange.startWeek.toString();
                } else {
                    return `${timelineViewRange.startWeek}-${timelineViewRange.endWeek}`;
                }
            }

            // Priority 2: Use selected period's week range
            if (selectedPeriodId && timePeriods.length > 0) {
                const selectedPeriod = timePeriods.find(p => String(p.id) === String(selectedPeriodId));
                if (selectedPeriod && selectedPeriod.start_week && selectedPeriod.end_week) {
                    logger.debug('[ClassSchedulerPage] Using selected period range for drag-to-create:',
                        selectedPeriod.start_week, '-', selectedPeriod.end_week);

                    // Format as week series string
                    if (selectedPeriod.start_week === selectedPeriod.end_week) {
                        return selectedPeriod.start_week.toString();
                    } else {
                        return `${selectedPeriod.start_week}-${selectedPeriod.end_week}`;
                    }
                }
            }

            // Priority 3: Fallback to full year (will be handled by default in useDragToCreate)
            logger.debug('[ClassSchedulerPage] No specific range available, using default');
            return null;
        }
    });

    // Get active days from AssetManager
    const { activeDays } = useActiveDays();

    // Initialize currentVisibleDay when activeDays is loaded
    useEffect(() => {
        if (activeDays.length > 0 && !currentVisibleDay) {
            setCurrentVisibleDay(activeDays[0]);
            setPreviousDay(activeDays[0]);
        }
    }, [activeDays, currentVisibleDay]);

    // Function to determine animation direction for day header transitions
    const getAnimationDirection = useCallback(() => {
        if (!activeDays || activeDays.length === 0 || !currentVisibleDay || !previousDay) return 'slideUp';
        const currentIndex = activeDays.indexOf(currentVisibleDay);
        const previousIndex = activeDays.indexOf(previousDay);
        // If either index is -1 (not found), default to slideUp
        if (currentIndex === -1 || previousIndex === -1) return 'slideUp';
        return currentIndex > previousIndex ? 'slideUp' : 'slideDown';
    }, [currentVisibleDay, previousDay, activeDays]);

    // --- Initialize the custom hook for drag and drop ---
    // Pass addLogEntry to enable change tracking in the drag and drop system
    // Use real data instead of mock data
    const {
        draggedItemInfo,
        availabilityOverlayData,
        handleGlobalDragStart,
        handleGlobalDragEnd,
        handleItemDrop: handleCalendarItemDrop, // Renaming to distinguish from potential sidebar drop
    } = useScheduleDragAndDrop(scheduledInstances, setScheduledInstances, user, addLogEntry, teachers, groups, subjects, rooms, {
        autoApplyToAllWeeks: true, // ClassSchedulerPage should automatically apply changes to all weeks
        activeDays,
        timelineContext: derivedTimelineContext
    });

    // Memoize expensive week series parsing to avoid repeated calculations
    const memoizedWeekSeriesParsing = useMemo(() => {
        const cache = new Map();
        return (weekSeries) => {
            if (!cache.has(weekSeries)) {
                cache.set(weekSeries, parseWeekSeries(weekSeries));
            }
            return cache.get(weekSeries);
        };
    }, []);

    // Memoize expensive date range calculations
    const memoizedDateRangeCalculation = useMemo(() => {
        const cache = new Map();
        return (year, weekNum) => {
            const key = `${year}-${weekNum}`;
            if (!cache.has(key)) {
                cache.set(key, getWeekDateRange(year, weekNum));
            }
            return cache.get(key);
        };
    }, []);

    // Simple flag to track if we have set the initial baseline
    // Provided by useClassSchedulerState

    // Additional flag to track if conflicts have been calculated for initial data
    // hasInitialConflictsCalculated provided by useClassSchedulerState

    // MODIFIED: Effect to compute filteredScheduledInstances based on selected time frame
    // IMPORTANT: Do NOT overwrite scheduledInstances (source of truth). Only set filteredScheduledInstances.
    useEffect(() => {
        console.log('*** MAIN FILTERING EFFECT (VIEW-ONLY) TRIGGERED ***', {
            timestamp: new Date().toISOString(),
            isFetchingDefinitions,
            isFetchingYearStructure,
            selectedPeriodId,
            selectedMonth: selectedMonth?.month,
            selectedWeek: selectedWeek?.week,
            scheduledInstancesLen: scheduledInstancesRef.current?.length || 0
        });

        // Wait for year structure when needed (whole-year case uses it)
        if (!selectedPeriodId && isFetchingYearStructure) {
            logger.debug('[ClassSchedulerPage] Waiting for year structure for whole year filtering...');
            return;
        }

        const baseList = Array.isArray(scheduledInstancesRef.current)
            ? scheduledInstancesRef.current
            : [];

        // Helper to optionally apply timeline range on top of selection filtering
        const applyTimelineRangeIfAny = (entries) => {
            if (!timelineViewRange) return entries;
            const filtered = entries.filter(entry => {
                if (!entry.weekSeries || !entry.year) {
                    return false; // Exclude items without series when timeline range is active
                }
                const entryWeekNumbers = memoizedWeekSeriesParsing(entry.weekSeries);
                if (!entryWeekNumbers.length) return false;
                return entryWeekNumbers.some(weekNum => timelineViewRange.isWeekInRange(weekNum, entry.year));
            });
            logger.debug(`[ClassSchedulerPage] Timeline range ${timelineViewRange.startWeek}-${timelineViewRange.endWeek}: showing ${filtered.length}/${entries.length} after selection filter`);
            return filtered;
        };

        // Week selection takes priority
        if (selectedWeek) {
            logger.debug('[ClassSchedulerPage] View filter by week:', selectedWeek.week);
            const weekNumber = selectedWeek.week;
            const weekYear = selectedWeek.year;

            // Apply week-specific exceptions for display while preserving original IDs for actions
            const withExceptionsApplied = baseList
                .map(item => {
                    if (!item || typeof item.isScheduled !== 'function' || !item.isScheduled()) {
                        // Keep unscheduled or invalid items as-is
                        return item;
                    }
                    if (typeof item.getEntryForWeek === 'function') {
                        const result = item.getEntryForWeek(weekNumber, weekYear);
                        if (!result) return null; // cancelled for this week
                        // Preserve original ID to keep resize/move/delete working on root entries
                        if (result.id !== item.id) {
                            try {
                                const resObj = typeof result.toObject === 'function' ? result.toObject() : result;
                                const newInstance = new ScheduleEntry({
                                    ...resObj,
                                    id: item.id
                                });
                                try { newInstance._exceptionContext = { originalId: item.id, week: weekNumber, year: weekYear }; } catch (_) { }
                                return newInstance;
                            } catch (e) {
                                try {
                                    result.id = item.id;
                                    // Attach exception context so the editor can detect it
                                    result._exceptionContext = { originalId: item.id, week: weekNumber, year: weekYear };
                                } catch (_) { }
                                return result;
                            }
                        }
                        return result;
                    }
                    return item;
                })
                .filter(Boolean);

            // Now filter to entries active in the selected week/year
            const weekFiltered = withExceptionsApplied.filter(item => {
                if (!item || typeof item.isScheduled !== 'function' || !item.isScheduled()) return true;
                // Match against academic year context, not the calendar year of week 1 in cross-year cases
                if (item.year !== selectedYearForView) return false;
                if (typeof item.isActiveForWeekSync === 'function') {
                    return item.isActiveForWeekSync(weekNumber, weekYear);
                }
                if (item.weekSeries) {
                    const entryWeekNumbers = memoizedWeekSeriesParsing(item.weekSeries);
                    return entryWeekNumbers.includes(weekNumber);
                }
                return true;
            });

            const finalFiltered = applyTimelineRangeIfAny(weekFiltered);
            setFilteredScheduledInstances(finalFiltered);
            return;
        }

        // Month selection next
        if (selectedMonth) {
            logger.debug('[ClassSchedulerPage] View filter by month:', selectedMonth.month);
            const monthStartWeek = selectedMonth.weekRange.start;
            const monthEndWeek = selectedMonth.weekRange.end;

            const monthFiltered = baseList.filter(item => {
                if (!item.isScheduled()) return true;
                if (item.year !== selectedYearForView) return false;
                if (item.weekSeries) {
                    const entryWeekNumbers = memoizedWeekSeriesParsing(item.weekSeries);
                    if (entryWeekNumbers.length > 0) {
                        return entryWeekNumbers.some(weekNum => {
                            if (monthStartWeek <= monthEndWeek) {
                                return weekNum >= monthStartWeek && weekNum <= monthEndWeek;
                            } else {
                                return weekNum >= monthStartWeek || weekNum <= monthEndWeek;
                            }
                        });
                    }
                }
                return true;
            });

            const finalFiltered = applyTimelineRangeIfAny(monthFiltered);
            setFilteredScheduledInstances(finalFiltered);
            return;
        }

        // Whole year (no specific period selected)
        if (!selectedPeriodId) {
            logger.debug('[ClassSchedulerPage] View filter for whole year');
            const wholeYearFiltered = baseList.filter(item => {
                if (!item.isScheduled()) return true;
                if (item.year !== selectedYearForView) return false;
                if (yearStructure && yearStructure.startWeek && yearStructure.endWeek) {
                    if (item.weekSeries) {
                        const entryWeekNumbers = memoizedWeekSeriesParsing(item.weekSeries);
                        return entryWeekNumbers.some(weekNum =>
                            isWeekWithinAcademicYear(weekNum, yearStructure.startWeek, yearStructure.endWeek)
                        );
                    }
                }
                return true;
            });

            const finalFiltered = applyTimelineRangeIfAny(wholeYearFiltered);
            setFilteredScheduledInstances(finalFiltered);
            return;
        }

        // Specific period selection
        if (!timePeriods.length) {
            logger.debug('[ClassSchedulerPage] No periods available, clearing view');
            setFilteredScheduledInstances([]);
            return;
        }

        const selectedPeriod = timePeriods.find(p => String(p.id) === String(selectedPeriodId));
        if (!selectedPeriod) {
            console.warn('[ClassSchedulerPage] Selected period not found:', selectedPeriodId);
            setFilteredScheduledInstances([]);
            return;
        }

        logger.debug('[ClassSchedulerPage] View filter by period:', { id: selectedPeriodId, name: selectedPeriod.name });
        const periodDateRange = selectedPeriod.getDateRange();

        const periodFiltered = baseList.filter(item => {
            if (!item.isScheduled()) return true;
            if (!item.weekSeries || !item.year) return false;
            const entryWeekNumbers = memoizedWeekSeriesParsing(item.weekSeries);
            if (!entryWeekNumbers.length) return false;
            for (const weekNum of entryWeekNumbers) {
                const entryWeekDateRange = memoizedDateRangeCalculation(item.year, weekNum);
                if (entryWeekDateRange && doDateRangesOverlap(periodDateRange, entryWeekDateRange)) {
                    return true;
                }
            }
            return false;
        });

        const finalFiltered = applyTimelineRangeIfAny(periodFiltered);
        setFilteredScheduledInstances(finalFiltered);
    }, [selectedPeriodId, selectedMonth, selectedWeek, isFetchingYearStructure, selectedYearForView, yearStructure, isWeekWithinAcademicYear, memoizedWeekSeriesParsing, memoizedDateRangeCalculation, timelineViewRange, scheduledInstances]);

    // Net-change calculation is now managed centrally via the controller when needed

    // --- Conflict Calculation Logic ---
    // Use a ref to track the last calculated items to prevent unnecessary recalculations
    const lastConflictCalculationRef = useRef(null);

    const calculateAllConflicts = useCallback(async (itemsToCalculate) => {
        if (!isAdmin || isCalculatingConflicts) return;

        const safeScheduleItems = Array.isArray(itemsToCalculate) ? itemsToCalculate : [];
        const scheduledItems = safeScheduleItems.filter(item => item.isScheduled());

        if (scheduledItems.length === 0) {
            setCurrentConflicts([]);

            // If no scheduled items, just set the flag that conflicts have been calculated
            // Baseline is now set immediately when data loads
            if (!hasInitialConflictsCalculated) {
                logger.debug('[ClassSchedulerPage] No scheduled items for conflict calculation, marking conflicts as calculated');
                setHasInitialConflictsCalculated(true);
            }

            return;
        }

        // Check if we've already calculated conflicts for these exact items
        const itemsSignature = scheduledItems.map(item => `${item.id}-${item.timeStart}-${item.timeEnd}-${item.room}`).join('|');
        if (lastConflictCalculationRef.current === itemsSignature) {
            return; // Skip if already calculated
        }

        setIsCalculatingConflicts(true);
        logger.debug("[ClassSchedulerPage] Recalculating all conflicts using ConflictManager...");

        try {
            // Use ConflictManager to recalculate conflicts for all entries
            const updatedEntries = await conflictManager.calculateConflictsForEntries(
                scheduledItems,
                teachers,
                groups,
                subjects
            );

            // Extract conflicts for the conflict list display
            const conflicts = [];
            updatedEntries.forEach(entry => {
                const overallStatus = entry.getOverallConflictStatus();
                if (overallStatus === 'red' || overallStatus === 'orange') {
                    // Collect all conflict reasons from all weeks with week information
                    const allReasonsWithWeeks = [];

                    if (entry.conflictsByWeek && entry.conflictsByWeek.size > 0) {
                        entry.conflictsByWeek.forEach((weekConflict, weekKey) => {
                            if (weekConflict.reasons && weekConflict.reasons.length > 0) {
                                // Add week information to each reason if not already present
                                const reasonsWithWeek = weekConflict.reasons.map(reason => {
                                    // Check if the reason already includes week information
                                    const hasWeekInfo = /week \d+/i.test(reason.reason) ||
                                        /\(week \d+\)/i.test(reason.reason);

                                    return {
                                        ...reason,
                                        reason: hasWeekInfo
                                            ? reason.reason
                                            : `${reason.reason} (week ${weekConflict.week})`
                                    };
                                });
                                allReasonsWithWeeks.push(...reasonsWithWeek);
                            }
                        });
                    }

                    // If no conflicts found, add a generic reason
                    const finalReasons = allReasonsWithWeeks.length > 0
                        ? allReasonsWithWeeks
                        : [{ reason: `Status: ${overallStatus}`, severity: overallStatus }];

                    conflicts.push({
                        itemId: entry.id,
                        itemName: entry.displayName,
                        day: entry.day,
                        time: entry.timeRangeDisplay(),
                        room: entry.room,
                        reasons: finalReasons
                    });
                }
            });

            logger.debug("[ClassSchedulerPage] Conflicts found:", conflicts.length);
            setCurrentConflicts(conflicts);

            // Update the scheduled instances with new conflict information
            setScheduledInstances(prevItems => {
                console.log('*** CONFLICT CALCULATION - UPDATING SCHEDULED INSTANCES ***', {
                    prevItemsLength: prevItems.length,
                    updatedEntriesLength: updatedEntries.length,
                    itemsSignature
                });

                // Create a map of updated entries for efficient lookup
                const updatedEntriesMap = new Map(updatedEntries.map(entry => [entry.id, entry]));

                // Update items with new conflict information
                const updatedItems = prevItems.map(item => {
                    if (item.isScheduled()) {
                        const updatedEntry = updatedEntriesMap.get(item.id);
                        if (updatedEntry) {
                            // Compare conflict status to check if there are changes
                            const prevStatus = item.getOverallConflictStatus();
                            const newStatus = updatedEntry.getOverallConflictStatus();

                            if (prevStatus !== newStatus) {
                                return updatedEntry;
                            }
                        }
                    }
                    return item;
                });

                console.log('*** CONFLICT CALCULATION - UPDATED ITEMS RESULT ***', {
                    updatedItemsLength: updatedItems.length,
                    sampleIds: updatedItems.slice(0, 5).map(item => item?.id)
                });

                lastConflictCalculationRef.current = itemsSignature;

                // Baseline is now set immediately when data loads, not here
                console.log('[ClassSchedulerPage] *** CONFLICT CALCULATION COMPLETE - BASELINE ALREADY SET ***', {
                    isBaselineSet,
                    hasInitialConflictsCalculated,
                    updatedItemsLength: updatedItems.length
                });

                // Set the flag that conflicts have been calculated
                setHasInitialConflictsCalculated(true);

                return updatedItems;
            });

        } catch (error) {
            console.error('[ClassSchedulerPage] Error calculating conflicts:', error);
            setCurrentConflicts([]);
        } finally {
            setIsCalculatingConflicts(false);
        }
    }, [isAdmin, isCalculatingConflicts, teachers, groups, subjects, isBaselineSet, hasInitialConflictsCalculated, courseDefinitions, isFetchingDefinitions]);

    // Debounced effect for conflict calculation
    useEffect(() => {
        if (!isAdmin) {
            setCurrentConflicts([]);
            return;
        }

        // Clear any existing timeout
        if (conflictCalculationTimeoutId) {
            clearTimeout(conflictCalculationTimeoutId);
        }

        // Set a new timeout to debounce the calculation
        const timeoutId = setTimeout(() => {
            calculateAllConflicts(scheduledInstances);
        }, 300); // 300ms debounce

        setConflictCalculationTimeoutId(timeoutId);

        // Cleanup function
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [scheduledInstances, isAdmin]); // Only depend on scheduledInstances and isAdmin

    const toggleConflictList = () => setIsConflictListVisible(prev => !prev);

    // Timeline range is handled inside the main view-only filtering effect.
    const handleTimelineRangeExternalChange = useCallback((rangeData) => {
        setTimelineViewRange(rangeData);
        if (hasUnsavedChanges) {
            setSuccess('You have unsaved changes. Reloading or leaving the page will discard them.');
            setTimeout(() => setSuccess(null), 2500);
        }
    }, [hasUnsavedChanges, setSuccess]);

    // View filtering is centralized in the main effect; no-op here to avoid overrides.
    useEffect(() => {
        // Intentionally left blank
    }, [scheduledInstances, timelineViewRange]);

    // Apply free-text filtering on top of timeline-based filtering
    const textFilteredScheduledInstances = useMemo(() => {
        const query = (textFilterQuery || '').trim().toLowerCase();
        if (!query) return filteredScheduledInstances;

        const stringIncludes = (value) => {
            if (!value || typeof value !== 'string') return false;
            return value.toLowerCase().includes(query);
        };

        return filteredScheduledInstances.filter(entry => {
            // Collect candidate strings
            const hostCandidates = [
                entry.teacher,
                entry?.additionalProps?.host,
                entry?.additionalProps?.eventHost,
                entry?.additionalProps?.event_host
            ];

            const groupNames = Array.isArray(entry.groups)
                ? entry.groups.map(g => {
                    if (g && typeof g === 'object') {
                        return String(g.name || g.group_code || '');
                    }
                    return g != null ? String(g) : '';
                }).filter(Boolean)
                : [];

            // Multi-teacher support: include formatted teacher list and individual names
            const teachersFormatted = typeof entry.teachersFormatted === 'string' ? entry.teachersFormatted : '';
            const teacherNamesMulti = (() => {
                // Prefer model helper when available to avoid duplicating logic
                if (typeof entry?.hasTeacher === 'function') {
                    // We still want candidate strings for text search; derive from arrays
                }
                const raw = (entry && (entry.teachers || entry.teachersArray)) || [];
                if (!Array.isArray(raw)) return [];
                return raw.map(t => {
                    if (t && typeof t === 'object') return String(t.name || '');
                    return t != null ? String(t) : '';
                }).filter(Boolean);
            })();

            const candidates = [
                entry.subject,
                entry.course,
                entry.title,
                entry.eventName,
                entry.description,
                ...hostCandidates,
                ...groupNames,
                teachersFormatted,
                ...teacherNamesMulti
            ].filter(v => typeof v === 'string' && v.trim() !== '');

            // If all relevant fields are empty, do not exclude this entry
            if (candidates.length === 0) return true;

            // Match if any candidate contains the query
            return candidates.some(stringIncludes);
        });
    }, [filteredScheduledInstances, textFilterQuery]);

    const entryTypeFilteredScheduledInstances = useMemo(() => {
        const enabledTypes = Object.entries(entryTypeFilters || {})
            .filter(([, enabled]) => enabled)
            .map(([key]) => key);
        const totalTypes = Object.keys(entryTypeFilters || {}).length;

        if (totalTypes === 0 || enabledTypes.length === totalTypes) {
            return textFilteredScheduledInstances;
        }

        return textFilteredScheduledInstances.filter(entry => {
            const rawType = (entry && typeof entry.type === 'string'
                ? entry.type
                : typeof entry?.entry_type === 'string'
                    ? entry.entry_type
                    : '').toLowerCase();

            const normalizedKey = rawType === 'class'
                ? 'class'
                : rawType === 'meeting'
                    ? 'meeting'
                    : rawType === 'event'
                        ? 'event'
                        : null;

            if (!normalizedKey) {
                // If the type is unknown, keep it visible
                return true;
            }

            return !!entryTypeFilters?.[normalizedKey];
        });
    }, [textFilteredScheduledInstances, entryTypeFilters]);

    // MODIFIED: unscheduledCourses are those from text-filtered working set that are not scheduled
    const unscheduledCourses = useMemo(() => {
        return entryTypeFilteredScheduledInstances.filter(item => !item.isScheduled());
    }, [entryTypeFilteredScheduledInstances]);

    // --- Specific Drop Handler for ClassSchedulerPage ---
    // This handler will decide if it's a new item from the sidebar or an existing item being moved.
    const handleActualItemDrop = useCallback(async (droppedItemId, targetRoomId, targetDay, newStartTimeString) => {
        // Resolve room ID to room object for logging and template creation
        const targetRoom = rooms.find(room => room.id === targetRoomId);
        const targetRoomName = targetRoom?.name || `Room ID ${targetRoomId}`;

        logger.debug(`[ClassScheduler] handleActualItemDrop called with:`, {
            droppedItemId,
            targetRoomId,
            targetRoomName,
            targetDay,
            newStartTimeString,
            draggedItemInfo
        });

        // Check both the drag metadata and the original data for the type
        const itemType = draggedItemInfo.typeDnd || draggedItemInfo.entry?.typeDnd;

        logger.debug(`[ClassScheduler] Drop detected: ID=${droppedItemId}, Type=${itemType}, RoomId=${targetRoomId}, RoomName=${targetRoomName}, Day=${targetDay}, Time=${newStartTimeString}`);

        if (itemType === ItemTypes.COURSE) { // Legacy code path for templates (currently unused)
            logger.debug(`[ClassScheduler] Creating new instance from Course Definition: ID=${draggedItemInfo.entry?.id}`);
            const courseDef = draggedItemInfo.entry; // This is now a ScheduleEntry instance

            if (!courseDef || !courseDef.durationMinutes) { // Check durationMinutes from the ScheduleEntry template
                console.error("Cannot create instance: Course definition or its duration is invalid.", courseDef);
                handleGlobalDragEnd();
                return;
            }

            // const { durationMinutes } = parseTime(courseDef.time); // Old way, courseDef.time was duration string
            const durationMinutes = courseDef.durationMinutes; // Directly use durationMinutes
            const { startMinutes: newStartMinutes } = parseTime(newStartTimeString);

            if (durationMinutes == null || newStartMinutes == null) {
                console.error("Cannot parse time for new instance or target slot.");
                handleGlobalDragEnd();
                return;
            }

            const newEndTimeString = formatTimeFromMinutes(newStartMinutes + durationMinutes);

            // Create a template item for the modal with the drop location and duration
            // Preserve the original ID in additional props so we can remove it from unscheduled list
            const templateItem = new ScheduleEntry({
                id: `temp-add-${Date.now()}`,
                day: targetDay,
                timeStart: newStartTimeString,
                timeEnd: newEndTimeString,
                course: courseDef.course || '',
                teacher: courseDef.teacher || '',
                group: courseDef.group || '',
                groups: courseDef.groups || [],
                room: targetRoomName,
                room_id: targetRoomId,
                type: courseDef.type || 'class',
                weekSeries: courseDef.weekSeries || '1-53',
                // Academic-year start year (e.g. 2025 for "2025-2026")
                year: selectedYearForView,
                // DON'T automatically set timePeriodId - preserve original weekSeries intention
                durationMinutes: durationMinutes,
                originalUnscheduledId: courseDef.id // Store the original ID for cleanup
            });

            // Open the modal with the pre-filled template (auto-save pending changes first in draft mode)
            await openModalWithAutosave(templateItem);
            handleGlobalDragEnd();

        } else { // Item dragged from calendar OR unscheduled container (existing scheduled instance)
            logger.debug(`[ClassScheduler] Moving existing scheduled item: ID=${droppedItemId}`);
            // Use the handler from the hook for moving existing items
            handleCalendarItemDrop(droppedItemId, targetRoomId, targetDay, newStartTimeString);
        }
        // handleGlobalDragEnd(); // The specific handlers (handleCalendarItemDrop or logic above) should call this.
    }, [draggedItemInfo, selectedPeriodId, selectedYearForView, handleGlobalDragEnd, handleCalendarItemDrop, setSelectedScheduleItem, setIsScheduleModalOpen, openModalWithAutosave]);

    // --- Resize Handler ---
    const handleItemResize = useCallback(async (itemId, newStartTime, newEndTime) => {
        logger.debug(`[ClassScheduler] handleItemResize called:`, {
            itemId,
            newStartTime,
            newEndTime
        });

        // Find the item to resize
        const itemToResize = scheduledInstances.find(item => item.id === itemId);
        if (!itemToResize) {
            console.error(`[ClassScheduler] Item not found for resize: ${itemId}`);
            return;
        }

        // Create updated item with new times
        const updatedItem = itemToResize.clone();
        updatedItem.timeStart = newStartTime;
        updatedItem.timeEnd = newEndTime;

        // Update the schedule instances
        const updatedInstances = scheduledInstances.map(item =>
            item.id === itemId ? updatedItem : item
        );

        console.log('*** HANDLEITEMRESIZE - UPDATING INSTANCES ***', {
            itemId,
            currentInstancesLength: scheduledInstances.length,
            updatedInstancesLength: updatedInstances.length,
            sampleIds: updatedInstances.slice(0, 5).map(item => item.id)
        });

        // Update state immediately for responsive UI (Requirement 8.4)
        setScheduledInstances(updatedInstances);

        // Add to change tracking log (Requirement 8.3)
        addLogEntry('modify', itemToResize, updatedItem);

        // Recalculate conflicts for the modified entry and affected entries (Requirements 7.1, 7.2, 7.3)
        try {
            const entriesWithUpdatedConflicts = await conflictManager.recalculateConflictsForChangedEntries(
                [itemId],
                updatedInstances,
                teachers,
                groups,
                subjects
            );

            console.log('*** HANDLEITEMRESIZE - CONFLICT RECALCULATION RESULT ***', {
                entriesWithUpdatedConflictsLength: entriesWithUpdatedConflicts.length,
                sampleIds: entriesWithUpdatedConflicts.slice(0, 5).map(item => item.id)
            });

            // Update state with conflict information using functional merge to preserve ID swaps
            const updatedMap = new Map(entriesWithUpdatedConflicts.map(e => [e.id, e]));
            setScheduledInstances(prevItems => prevItems.map(it => updatedMap.get(it.id) || it));

            logger.debug(`[ClassScheduler] Resize completed with conflict recalculation for item: ${itemId}`);
        } catch (error) {
            console.error(`[ClassScheduler] Error recalculating conflicts after resize:`, error);
            // Keep the time changes even if conflict calculation fails
        }
    }, [scheduledInstances, teachers, groups, subjects, addLogEntry]);

    // --- Modal Handlers ---
    const closeScheduleModal = useCallback(() => {
        setIsScheduleModalOpen(false);
        setSelectedScheduleItem(null);
    }, []);

    const handleSaveEdit = useCallback(async (editedDataObj, originalItem) => {
        setIsSaveAnimationActive(true);
        try {
            // Build merged entry
            const sourceObj = originalItem && typeof originalItem.toObject === 'function' ? originalItem.toObject() : (originalItem || {});
            const mergedData = { ...(sourceObj || {}), ...(editedDataObj || {}) };
            const mergedEntry = new ScheduleEntry(mergedData);

            // Check if this is a new item (for highlighting logic)
            const isNewItem = !originalItem || String(originalItem.id).startsWith('temp-add-');

            // Compute updated items list
            const prevItems = Array.isArray(scheduledInstances) ? scheduledInstances : [];
            const exists = prevItems.some(it => String(it.id) === String(mergedEntry.id));
            const updatedItems = exists
                ? prevItems.map(it => String(it.id) === String(mergedEntry.id) ? mergedEntry : it)
                : [...prevItems, mergedEntry];

            // Update local state for responsive UI
            setScheduledInstances(updatedItems);

            // Change tracking
            if (!exists) {
                addLogEntry('create', null, mergedEntry);
            } else {
                addLogEntry('modify', originalItem, mergedEntry);
            }

            // Set highlighted item ID for scrolling and highlighting (only for new items and scheduled items)
            if (isNewItem && mergedEntry.isScheduled()) {
                setHighlightedItemId(mergedEntry.id);
            }

            // Persist only in draft mode
            if (!isLiveMode) {
                await handleSaveChanges(updatedItems, saveChangesToAPI, () => {
                    setDraftRefreshTrigger(prev => prev + 1);
                    setIsBaselineSet(false);
                    setHasInitialConflictsCalculated(false);
                });
            }

            // Recalculate conflicts in both modes
            if (isAdmin) {
                conflictManager.recalculateConflictsForChangedEntries(
                    [mergedEntry.id],
                    updatedItems,
                    teachers,
                    groups,
                    subjects
                ).then(entriesWithUpdatedConflicts => {
                    const updatedMap = new Map(entriesWithUpdatedConflicts.map(e => [e.id, e]));
                    setScheduledInstances(prevItems => prevItems.map(it => updatedMap.get(it.id) || it));
                }).catch(() => { });
            }
        } finally {
            closeScheduleModal();
            // Keep animation active for a bit longer to show the effect
            setTimeout(() => setIsSaveAnimationActive(false), 1000);
        }
    }, [isLiveMode, scheduledInstances, setScheduledInstances, addLogEntry, handleSaveChanges, saveChangesToAPI, setDraftRefreshTrigger, setIsBaselineSet, setHasInitialConflictsCalculated, isAdmin, teachers, groups, subjects, closeScheduleModal]);

    // --- Time Period Management Handlers ---
    // Period management has been moved to Year Structure Editor
    // These handlers are no longer needed

    // Placeholder for opening item modal
    const handleOpenModal = useCallback(async (item) => {
        logger.debug("Open modal for (ClassSchedulerPage):", item);
        await openModalWithAutosave(item);
    }, [openModalWithAutosave]);

    // Duplicate an existing scheduled item (creates a new entry with a new ID)
    const handleDuplicateItem = useCallback((item) => {
        try {
            const sourceObj = typeof item?.toObject === 'function' ? item.toObject() : item;
            const newItemId = `item-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
            const newItemData = { ...sourceObj, id: newItemId };
            const newEntry = new ScheduleEntry(newItemData);

            setScheduledInstances(prevItems => {
                console.log('*** HANDLEDUPLICATEITEM - ADDING DUPLICATE ***', {
                    prevItemsLength: prevItems.length,
                    newItemId: newEntry.id,
                    originalItemId: item.id,
                    sampleIds: prevItems.slice(0, 5).map(item => item.id)
                });

                const updatedItems = [...prevItems, newEntry];

                console.log('*** HANDLEDUPLICATEITEM - ADDED DUPLICATE RESULT ***', {
                    updatedItemsLength: updatedItems.length,
                    sampleIds: updatedItems.slice(0, 5).map(item => item.id)
                });

                if (isAdmin) {
                    conflictManager.recalculateConflictsForChangedEntries(
                        [newEntry.id],
                        updatedItems,
                        teachers,
                        groups,
                        subjects
                    ).then(entriesWithUpdatedConflicts => {
                        console.log('*** HANDLEDUPLICATEITEM - CONFLICT RECALCULATION RESULT ***', {
                            entriesWithUpdatedConflictsLength: entriesWithUpdatedConflicts.length,
                            sampleIds: entriesWithUpdatedConflicts.slice(0, 5).map(item => item.id)
                        });
                        const updatedMap = new Map(entriesWithUpdatedConflicts.map(e => [e.id, e]));
                        setScheduledInstances(prevItems => prevItems.map(it => updatedMap.get(it.id) || it));
                    });
                }
                return updatedItems;
            });

            addLogEntry('create', null, newEntry);
            logger.debug('Item duplicated', { from: item?.id, to: newEntry.id });
        } catch (error) {
            console.error('Failed to duplicate item:', error);
        }
    }, [isAdmin, teachers, groups, subjects]);

    // Core deletion logic - shared by both grid and modal deletions
    const deleteScheduleItem = useCallback(async (scheduleItem) => {
        console.log('*** DELETESCHEDULEITEM CALLED ***', {
            itemId: scheduleItem.id,
            itemCourse: scheduleItem.course,
            currentScheduledInstancesLength: scheduledInstances.length
        });

        logger.debug('Deleting schedule item:', scheduleItem.id);

        // Remove the item from scheduledInstances
        const updatedInstances = scheduledInstances.filter(item => item.id !== scheduleItem.id);

        console.log('*** DELETESCHEDULEITEM - FILTERING INSTANCES ***', {
            prevInstancesLength: scheduledInstances.length,
            itemToDelete: scheduleItem.id,
            updatedInstancesLength: updatedInstances.length,
            sampleIds: updatedInstances.slice(0, 5).map(item => item.id)
        });

        // Update local state for responsive UI
        setScheduledInstances(updatedInstances);

        // Track this item as locally deleted to prevent re-addition by filtering effects
        setLocallyDeletedItemIds(prev => {
            const newSet = new Set(prev);
            newSet.add(String(scheduleItem.id));
            console.log('*** DELETESCHEDULEITEM - ADDED TO LOCALLY DELETED SET ***', {
                itemId: scheduleItem.id,
                locallyDeletedCount: newSet.size
            });
            return newSet;
        });

        // Add to change log for tracking
        addLogEntry('delete', scheduleItem, null);

        // Persist immediately in draft mode (reusing the same pattern as handleSaveEdit)
        if (!isLiveMode) {
            await handleSaveChanges(updatedInstances, saveChangesToAPI, () => {
                setDraftRefreshTrigger(prev => prev + 1);
                setIsBaselineSet(false);
                setHasInitialConflictsCalculated(false);
            });
        }

        // Show success message
        setSuccess('Schedule item deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
    }, [addLogEntry, setSuccess, scheduledInstances, isLiveMode, handleSaveChanges, saveChangesToAPI, setDraftRefreshTrigger, setIsBaselineSet, setHasInitialConflictsCalculated]);

    // Delete schedule item from grid view (inline deletion)
    const handleDeleteFromGrid = useCallback(async (scheduleItem) => {
        await deleteScheduleItem(scheduleItem);
    }, [deleteScheduleItem]);

    // Delete schedule item from modal view (closes modal after deletion)
    const handleDeleteFromModal = useCallback(async (scheduleItem) => {
        await deleteScheduleItem(scheduleItem);
        closeScheduleModal();
    }, [deleteScheduleItem, closeScheduleModal]);

    // Delete unscheduled item from sidebar
    const handleDeleteUnscheduled = useCallback(async (scheduleItem) => {
        await deleteScheduleItem(scheduleItem);
    }, [deleteScheduleItem]);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setIsImporting(true);
            setImportError(null);
            setImportSuccess(null);

            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const fileExtension = file.name.split('.').pop().toLowerCase();

                    // Generate a draft name based on the file name and timestamp
                    const baseName = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
                    const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
                    const draftName = `Import-${baseName}-${timestamp}`;

                    let result;
                    if (fileExtension === 'csv') {
                        // Handle CSV file - import into draft
                        const csvData = e.target.result;
                        logger.debug('[Frontend] CSV Data type:', typeof csvData);
                        logger.debug('[Frontend] CSV Data length:', csvData ? csvData.length : 'N/A');
                        logger.debug('[Frontend] CSV Data sample:', csvData ? csvData.substring(0, 200) : 'N/A');
                        result = await draftManager.importIntoDraft(draftName, csvData, 'csv', selectedYearForView);
                    } else if (fileExtension === 'json') {
                        // Handle JSON file - import into draft
                        const scheduleEntries = JSON.parse(e.target.result);
                        result = await draftManager.importIntoDraft(draftName, scheduleEntries, 'json', selectedYearForView);
                    } else {
                        throw new Error('Unsupported file format. Please upload a .json or .csv file.');
                    }

                    setImportSuccess({
                        successful: result.successful_imports_count,
                        failed: result.failed_imports_count,
                        details: result.details,
                        draftName: draftName
                    });

                    // The DraftManager has switched to the new draft internally,
                    // and our listener will automatically update the component state
                    logger.debug('[ClassSchedulerPage] Import completed, DraftManager listener will handle mode switch');

                    // Show success message with draft information
                    setSuccess(`Successfully imported ${result.successful_imports_count} entries into draft "${draftName}". You are now viewing the draft.`);
                    setTimeout(() => setSuccess(null), 5000);

                } catch (error) {
                    console.error('Import failed:', error);
                    // Check if the error response contains detailed information
                    let errorMessage = 'Failed to import schedule entries';
                    if (error.response?.data?.error) {
                        const { line, reason } = error.response.data.error;
                        errorMessage = `Error on line ${line || 'unknown'}: ${reason}`;
                    } else if (error.message) {
                        errorMessage = error.message;
                    }
                    setImportError(errorMessage);
                } finally {
                    setIsImporting(false);
                }
            };

            reader.onerror = () => {
                setImportError('Failed to read the file');
                setIsImporting(false);
            };

            reader.readAsText(file);
        } catch (error) {
            console.error('File handling error:', error);
            setImportError(error.message || 'Failed to process the file');
            setIsImporting(false);
        }

        // Clear the file input so the same file can be uploaded again if needed
        event.target.value = '';
    };

    const handleExport = async () => {
        if (!user?.token) {
            setImportError('Authentication required for export');
            return;
        }

        setIsExporting(true);
        try {
            const filters = {
                timePeriodId: selectedPeriodId,
                // Add draft_name filter based on current mode
                draft_name: isLiveMode ? 'live' : currentDraftName,
                // Add any other filters you want to include
            };

            const blob = await exportScheduleEntries(filters);

            // Additional client-side filtering to ensure no placeholder entries are exported
            const blobText = await blob.text();
            const scheduleData = JSON.parse(blobText);

            // Filter out placeholder entries
            const filteredData = scheduleData.filter(entry =>
                entry.subject_name !== '__PLACEHOLDER__' &&
                entry.teacher_name !== '__PLACEHOLDER__' &&
                entry.room_name !== '__PLACEHOLDER__'
            );

            // Log filtering results
            const placeholderCount = scheduleData.length - filteredData.length;
            if (placeholderCount > 0) {
                console.log(`[Export] Filtered out ${placeholderCount} placeholder entries from export`);
            } else {
                console.log('[Export] No placeholder entries found - export clean');
            }

            // Create a new blob with filtered data
            const filteredBlob = new Blob([JSON.stringify(filteredData, null, 2)], { type: 'application/json' });

            // Create a download link and trigger the download
            const url = window.URL.createObjectURL(filteredBlob);
            const a = document.createElement('a');
            a.href = url;

            // Generate filename based on current mode
            const modeSuffix = isLiveMode ? 'live' : `draft-${currentDraftName}`;
            a.download = `schedule-export-${modeSuffix}-${new Date().toISOString().split('T')[0]}.json`;

            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Export failed:', error);
            setImportError('Failed to export schedule: ' + error.message);
        } finally {
            setIsExporting(false);
        }
    };

    // New handler to remove a scheduled item when dropped into the unscheduled container
    const handleRemoveScheduledItem = useCallback((itemId) => {
        logger.debug(`[ClassScheduler] Removing scheduled item: ID=${itemId}`);

        const itemToRemove = scheduledInstances.find(item => String(item.id) === String(itemId));
        // Also check courseDefinitions if the item might have been a template directly dragged.
        // However, handleRemoveScheduledItem is usually called for items *from the schedule*.
        const definitionContextItem = courseDefinitions.find(def => def.id === itemId);


        if (!itemToRemove && !definitionContextItem) {
            console.error(`Cannot find item with ID: ${itemId} in scheduledInstances or courseDefinitions.`);
            handleGlobalDragEnd();
            return;
        }

        // Prefer itemToRemove if found (it was on the schedule), otherwise use definitionContextItem
        // This logic might need refinement based on where items can be dragged from to the unscheduled bin
        const sourceItem = itemToRemove || definitionContextItem;


        // Defer state updates to allow react-dnd to finish its current cycle
        setTimeout(() => {
            // Create ScheduleEntry instances for change tracking
            const beforeItem = sourceItem instanceof ScheduleEntry ? sourceItem : new ScheduleEntry(sourceItem);
            const afterItem = beforeItem.unschedule(); // Use the new unschedule method

            logger.debug(`[ClassScheduler] UNSCHEDULING DEBUG for item ${itemId}:`);
            logger.debug(`[ClassScheduler] BEFORE unschedule:`, {
                id: beforeItem.id,
                day: beforeItem.day,
                timeStart: beforeItem.timeStart,
                timeEnd: beforeItem.timeEnd,
                room: beforeItem.room,
                isScheduled: beforeItem.isScheduled()
            });
            logger.debug(`[ClassScheduler] AFTER unschedule:`, {
                id: afterItem.id,
                day: afterItem.day,
                timeStart: afterItem.timeStart,
                timeEnd: afterItem.timeEnd,
                room: afterItem.room,
                isScheduled: afterItem.isScheduled()
            });

            // Add to change log to track the unscheduling
            addLogEntry('modify', beforeItem, afterItem);

            // Update the item in scheduledInstances to be unscheduled (don't remove it completely)
            if (itemToRemove) {
                setScheduledInstances(prevInstances => {
                    console.log('*** HANDLEREMOVESCHEDULEDITEM - UNSCHEDULING ITEM ***', {
                        itemId,
                        prevInstancesLength: prevInstances.length,
                        sampleIds: prevInstances.slice(0, 5).map(item => item.id)
                    });

                    const updatedInstances = prevInstances.map(item => item.id === itemId ? afterItem : item);

                    console.log('*** HANDLEREMOVESCHEDULEDITEM - UNSCHEDULED RESULT ***', {
                        updatedInstancesLength: updatedInstances.length,
                        sampleIds: updatedInstances.slice(0, 5).map(item => item.id)
                    });

                    return updatedInstances;
                });
                logger.debug(`[ClassScheduler] Updated item ${itemId} to unscheduled state in scheduledInstances`);
            } else {
                // If it wasn't in scheduledInstances, add the unscheduled version
                setScheduledInstances(prevInstances => {
                    console.log('*** HANDLEREMOVESCHEDULEDITEM - ADDING UNSCHEDULED ITEM ***', {
                        itemId,
                        prevInstancesLength: prevInstances.length,
                        sampleIds: prevInstances.slice(0, 5).map(item => item.id)
                    });

                    const updatedInstances = [...prevInstances, afterItem];

                    console.log('*** HANDLEREMOVESCHEDULEDITEM - ADDED UNSCHEDULED RESULT ***', {
                        updatedInstancesLength: updatedInstances.length,
                        sampleIds: updatedInstances.slice(0, 5).map(item => item.id)
                    });

                    return updatedInstances;
                });
                logger.debug(`[ClassScheduler] Added unscheduled item ${itemId} to scheduledInstances`);
            }

            // Don't update courseDefinitions here - it represents server state and should only be updated
            // when fetching from server or after saving. Local changes only affect scheduledInstances.

            logger.debug("Item unscheduled/moved to definitions:", afterItem);
            handleGlobalDragEnd();
        }, 0);

    }, [scheduledInstances, courseDefinitions, setScheduledInstances, handleGlobalDragEnd, addLogEntry]);

    // Get available academic years from schedule data (start years of academic years)
    // availableYears provided by useClassSchedulerState

    // Set default room for room view when rooms are available (kept)

    // Memoize selected period object for room occupancy view
    const selectedPeriodForRoomView = useMemo(() => {
        // If a specific period is selected, use it
        if (selectedPeriodId) {
            const found = timePeriods.find(p => String(p.id) === String(selectedPeriodId));
            if (found) return found;
        }

        // Fallback: synthesize a full-year period using academic year structure
        const startWeek = yearStructure?.startWeek ?? 1;
        const endWeek = yearStructure?.endWeek ?? 53;
        const periodYear = selectedYearForView;

        const containsWeek = (week, year) => {
            if (startWeek <= endWeek) {
                return year === periodYear && week >= startWeek && week <= endWeek;
            }
            // Cross-year academic year
            return (year === periodYear && week >= startWeek) || (year === periodYear + 1 && week <= endWeek);
        };

        return {
            id: 'full-year',
            name: 'Full Year',
            start_week: startWeek,
            end_week: endWeek,
            year: periodYear,
            containsWeek
        };
    }, [selectedPeriodId, timePeriods, yearStructure, selectedYearForView]);

    // Set default teacher for teacher view when teachers are available
    useEffect(() => {
        if (teachers.length > 0 && !selectedTeacherForView) {
            setSelectedTeacherForView(teachers[0].name);
        }
    }, [teachers, selectedTeacherForView]);

    // Set default year based on available data
    useEffect(() => {
        if (availableYears.length > 0 && !availableYears.includes(selectedYearForView)) {
            // Set to the most recent year that has data
            const yearsWithData = availableYears.filter(year =>
                scheduledInstances.some(instance => instance.year === year)
            );
            if (yearsWithData.length > 0) {
                setSelectedYearForView(Math.max(...yearsWithData));
            }
        }
    }, [availableYears, selectedYearForView, scheduledInstances]);

    // Scroll tracking effect for fixed day header
    useEffect(() => {
        // Only set up scroll listener after loading is complete and activeDays is available
        if (isLoading || !activeDays || activeDays.length === 0) return;

        let ticking = false;

        // Create a throttled scroll handler to improve performance
        const scrollHandler = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    if (!scrollContainerRef.current) {
                        ticking = false;
                        return;
                    }

                    const scrollTop = scrollContainerRef.current.scrollTop;
                    let foundDay = activeDays[0]; // Default to the first day

                    // Safety check for activeDays
                    if (!activeDays || activeDays.length === 0) {
                        ticking = false;
                        return;
                    }

                    // Iterate through day headers to find the one currently at the top
                    for (const day of activeDays) {
                        const headerElement = dayHeaderRefs.current[day];
                        if (headerElement) {
                            const offsetTop = headerElement.offsetTop;
                            const headerHeight = headerElement.offsetHeight;
                            const activationOffset = headerHeight * 4;

                            // Add 60px offset to make day header switch earlier
                            if (scrollTop >= offsetTop - activationOffset - 60) {
                                foundDay = day;
                            } else {
                                break;
                            }
                        }
                    }

                    // Use functional updates to avoid stale closures
                    setCurrentVisibleDay(currentDay => {
                        if (foundDay !== currentDay) {
                            setPreviousDay(currentDay);
                            return foundDay;
                        }
                        return currentDay;
                    });

                    // Header hide/show logic based on inner vertical scroll
                    try {
                        const last = lastInnerScrollTopRef.current || 0;
                        const delta = scrollTop - last;
                        const smallThreshold = 5; // ignore tiny jitters

                        if (delta > smallThreshold) {
                            // Scrolling down inside scheduler
                            if (!setupHeaderHiddenRef.current) {
                                window.scrollTo({ top: 100, behavior: 'smooth' }); // hide Setup header
                                setupHeaderHiddenRef.current = true;
                                maxDownInnerScrollTopRef.current = scrollTop;
                            } else if (scrollTop > maxDownInnerScrollTopRef.current) {
                                maxDownInnerScrollTopRef.current = scrollTop;
                            }
                        } else if (delta < -smallThreshold) {
                            // Scrolling up inside scheduler
                            if (setupHeaderHiddenRef.current) {
                                // Reveal header when inner scroll reaches the very top
                                if (scrollTop <= 1) {
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                    setupHeaderHiddenRef.current = false;
                                    maxDownInnerScrollTopRef.current = 0;
                                }
                            }
                        }

                        lastInnerScrollTopRef.current = scrollTop;
                    } catch (_) { /* no-op */ }

                    ticking = false;
                });
                ticking = true;
            }
        };

        // Add listener with a delay to ensure DOM is ready
        const timer = setTimeout(() => {
            const scrollContainer = scrollContainerRef.current;
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', scrollHandler);
                scrollHandler(); // Initial call to set correct day
            } else {
                console.warn('[ClassSchedulerPage] scrollContainer ref is still null after loading completed');
            }
        }, 100); // Increased delay to ensure DOM is fully ready

        return () => {
            clearTimeout(timer);
            const scrollContainer = scrollContainerRef.current;
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', scrollHandler);
            }
        };
    }, [isLoading, activeDays]);

    // Scroll to highlighted item's day and highlight it
    useEffect(() => {
        if (!highlightedItemId || !scrollContainerRef.current) return;

        // Find the item in scheduled instances
        const highlightedItem = scheduledInstances.find(item => String(item.id) === String(highlightedItemId));
        if (!highlightedItem || !highlightedItem.day) return;

        const itemDay = highlightedItem.day;

        // Check if we need to scroll (item's day is different from current visible day)
        if (itemDay !== currentVisibleDay) {
            const dayHeader = dayHeaderRefs.current[itemDay];
            if (dayHeader && scrollContainerRef.current) {
                // Scroll to the day's header
                const headerTop = dayHeader.offsetTop;
                scrollContainerRef.current.scrollTo({
                    top: headerTop - 20, // 20px offset for better visibility
                    behavior: 'smooth'
                });

                logger.debug(`[ClassSchedulerPage] Scrolling to day: ${itemDay} for highlighted item: ${highlightedItemId}`);
            }
        }

        // Clear highlight after 2 seconds
        const timer = setTimeout(() => {
            setHighlightedItemId(null);
        }, 2500);

        return () => clearTimeout(timer);
    }, [highlightedItemId, scheduledInstances, currentVisibleDay]);

    // Week filtering handlers
    const handleWeekChange = (weekData) => {


        setSelectedWeek(weekData);
        logger.debug('[ClassSchedulerPage] Week selection changed:', weekData);

        // Clear period and month selection when week is selected
        if (weekData && selectedPeriodId) {
            setSelectedPeriodId(null);
        }
        if (weekData && selectedMonth) {
            setSelectedMonth(null);
        }

        // Clear teacher selection when week is selected (for teacher view)
        if (weekData && selectedTeacherForView) {
            setSelectedTeacherForView(null);
        }

        // Reset baseline when switching weeks so it can be set again
        setIsBaselineSet(false);
        setHasInitialConflictsCalculated(false);

        if (hasUnsavedChanges) {
            setSuccess('You have unsaved changes. Reloading or leaving the page will discard them.');
            setTimeout(() => setSuccess(null), 2500);
        }
    };

    // Month filtering handlers
    const handleMonthChange = (monthData) => {
        // Only show confirmation if there are unsaved changes AND we're actually changing to a different month
        // Don't show confirmation when clearing selections (monthData is null)


        setSelectedMonth(monthData);
        logger.debug('[ClassSchedulerPage] Month selection changed:', monthData);

        // Clear period and week selection when month is selected
        if (monthData && selectedPeriodId) {
            setSelectedPeriodId(null);
        }
        if (monthData && selectedWeek) {
            setSelectedWeek(null);
        }

        // Clear teacher selection when month is selected (for teacher view)
        if (monthData && selectedTeacherForView) {
            setSelectedTeacherForView(null);
        }

        // Reset baseline when switching months so it can be set again
        setIsBaselineSet(false);
        setHasInitialConflictsCalculated(false);

        if (hasUnsavedChanges) {
            setSuccess('You have unsaved changes. Reloading or leaving the page will discard them.');
            setTimeout(() => setSuccess(null), 2500);
        }
    };

    // Period change handler that clears month selection
    const handlePeriodChange = (periodId) => {
        // Only show confirmation if there are unsaved changes AND we're actually changing to a different period
        // Don't show confirmation when clearing selections (periodId is null)


        setSelectedPeriodId(periodId);

        // Clear month and week selection when period is selected
        if (periodId && selectedMonth) {
            setSelectedMonth(null);
        }
        if (periodId && selectedWeek) {
            setSelectedWeek(null);
        }

        // Clear teacher selection when period is selected (for teacher view)
        if (periodId && selectedTeacherForView) {
            setSelectedTeacherForView(null);
        }

        // Reset baseline when switching periods so it can be set again
        setIsBaselineSet(false);
        setHasInitialConflictsCalculated(false);

        if (hasUnsavedChanges) {
            setSuccess('You have unsaved changes. Reloading or leaving the page will discard them.');
            setTimeout(() => setSuccess(null), 2500);
        }
    };

    // Year change handler that checks for unsaved changes
    const handleYearChange = (year) => {
        // Check if there are unsaved changes


        setSelectedYearForView(year);

        // Clear teacher, month, and week selection when year changes
        if (selectedTeacherForView) {
            setSelectedTeacherForView(null);
        }
        if (selectedMonth) {
            setSelectedMonth(null);
        }
        if (selectedWeek) {
            setSelectedWeek(null);
        }

        // Reset baseline when switching years so it can be set again
        setIsBaselineSet(false);
        setHasInitialConflictsCalculated(false);

        if (hasUnsavedChanges) {
            setSuccess('You have unsaved changes. Reloading or leaving the page will discard them.');
            setTimeout(() => setSuccess(null), 2500);
        }
    };

    // Draft mode handlers
    // handleModeChange provided by useClassSchedulerState

    // handleDraftChange provided by useClassSchedulerState

    // Keep a stable reference for use before initialization sites
    useEffect(() => {
        saveChangesToAPIRef.current = saveChangesToAPI;
    }, [saveChangesToAPI]);

    // Clear locally deleted items when courseDefinitions changes (data refresh)
    useEffect(() => {
        if (courseDefinitions.length > 0) {
            console.log('*** CLEARING LOCALLY DELETED ITEMS ON DATA REFRESH ***', {
                courseDefinitionsLength: courseDefinitions.length,
                locallyDeletedCount: locallyDeletedItemIds.size
            });
            setLocallyDeletedItemIds(new Set());
        }
    }, [courseDefinitions.length]);

    // Seed scheduledInstances from courseDefinitions on initial load (or when empty)
    useEffect(() => {
        if (!isFetchingDefinitions && Array.isArray(courseDefinitions) && courseDefinitions.length > 0) {
            const currentLen = Array.isArray(scheduledInstancesRef.current) ? scheduledInstancesRef.current.length : 0;
            if (currentLen === 0) {
                logger.debug('[ClassSchedulerPage] Seeding scheduledInstances from courseDefinitions', {
                    seedLength: courseDefinitions.length,
                    isFetchingDefinitions,
                    courseDefinitionsIds: courseDefinitions.slice(0, 3).map(c => c.id)
                });
                setScheduledInstances(courseDefinitions);
            }
        }
    }, [isFetchingDefinitions, courseDefinitions, setScheduledInstances]);

    // Baseline is now set exclusively inside useChangeTracking when ready

    // Add handleSaveCurrent function
    const handleSaveCurrent = async () => {
        logger.debug('[ClassSchedulerPage] *** handleSaveCurrent CALLED ***', {
            currentDraftName,
            isLiveMode,
            changeLogLength: changeLog.length
        });

        if (!currentDraftName) {
            // For now, just show an error - we'll handle "Save As" later if needed
            setError('No draft name specified');
            return;
        }

        setIsLoading(true);
        setIsSaveAnimationActive(true);
        try {
            // In draft mode, save changes directly to the draft
            logger.debug('[ClassSchedulerPage] Saving current state directly to draft:', currentDraftName);

            // scheduledInstances now contains both scheduled and unscheduled items
            const scheduledCount = scheduledInstances.filter(item => item.isScheduled()).length;
            const unscheduledCount = scheduledInstances.length - scheduledCount;

            logger.debug('[ClassSchedulerPage] Saving', scheduledInstances.length, 'entries to draft:', {
                scheduled: scheduledCount,
                unscheduled: unscheduledCount
            });

            if (scheduledInstances.length > 0) {
                // Use the proper handleSaveChanges function to ensure baseline is updated immediately
                logger.debug('[ClassSchedulerPage] *** CALLING handleSaveChanges WITH IMMEDIATE BASELINE UPDATE ***');
                await handleSaveChanges(scheduledInstances, saveChangesToAPI);
                // Non-blocking background refresh to ensure UI reflects server state without rebuilding page
                try { await refreshScheduleEntries(); } catch (_) { }
            }

            // Don't set baseline here - let the data refresh handle it properly
        } catch (err) {
            console.error('[ClassSchedulerPage] Error saving current state:', err);
            setError('Failed to save draft: ' + err.message);
        } finally {
            setIsLoading(false);
            // Keep animation active for a bit longer to show the effect
            setTimeout(() => setIsSaveAnimationActive(false), 1000);
        }
    };

    // Add handlePublishDraft function with scope + strategy support
    const handlePublishDraft = async (publishConfig = {}) => {
        if (!currentDraftName) {
            setError('No draft selected to publish');
            return;
        }

        const normalizedPublishConfig = publishConfig && (
            publishConfig.weekRange !== undefined
            || publishConfig.publishMode !== undefined
            || publishConfig.reviewDecisions !== undefined
        )
            ? publishConfig
            : { weekRange: publishConfig };
        const weekRange = normalizedPublishConfig.weekRange || (
            normalizedPublishConfig.startWeek !== undefined && normalizedPublishConfig.endWeek !== undefined
                ? { startWeek: normalizedPublishConfig.startWeek, endWeek: normalizedPublishConfig.endWeek }
                : null
        );
        const publishMode = normalizedPublishConfig.publishMode || 'replace';
        const reviewDecisions = normalizedPublishConfig.reviewDecisions || {};

        setIsLoading(true);
        setIsSaveAnimationActive(true);
        try {
            // Step 1: Save the current draft first
            logger.debug('[ClassSchedulerPage] Saving draft before publishing:', currentDraftName);

            const scheduledCount = scheduledInstances.filter(item => item.isScheduled()).length;
            const unscheduledCount = scheduledInstances.length - scheduledCount;

            logger.debug('[ClassSchedulerPage] Saving', scheduledInstances.length, 'entries to draft before publishing:', {
                scheduled: scheduledCount,
                unscheduled: unscheduledCount,
                weekRange,
                publishMode,
                reviewDecisionsCount: Object.keys(reviewDecisions).length
            });

            if (scheduledInstances.length > 0) {
                // Save current changes to the draft
                await handleSaveChanges(scheduledInstances, saveChangesToAPI);
            }

            // Step 1b: Create a live backup before publishing (rolling 20) via manager
            try {
                await LiveBackupManager.backupCurrentLive(assetManager, draftManager, selectedYearForView, 20);
            } catch (backupErr) {
                console.warn('[ClassSchedulerPage] Failed to create backup before publish (non-fatal):', backupErr);
            }

            // Step 2: Publish the draft to live (with optional week range)
            logger.debug('[ClassSchedulerPage] Publishing draft to live:', {
                draftName: currentDraftName,
                weekRange,
                publishMode,
                reviewDecisionsCount: Object.keys(reviewDecisions).length
            });
            await assetManager.loadDraft(currentDraftName, selectedYearForView, {
                weekRange,
                publishMode,
                reviewDecisions
            });

            // Step 3: Stay in draft mode (don't switch to live mode)
            const rangeMessage = weekRange
                ? ` (weeks ${weekRange.startWeek}-${weekRange.endWeek})`
                : '';
            const strategyMessage = publishMode === 'additive' ? 'additively ' : '';

            setSuccess({
                message: `Draft published ${strategyMessage}to Live successfully${rangeMessage}!`,
                action: {
                    label: 'Go To Live',
                    onClick: () => {
                        draftManager.setLiveMode();
                        setSuccess(null);
                    }
                }
            });
            setTimeout(() => setSuccess(null), 5000); // Increased timeout to give time for button interaction

            // Trigger data refresh to ensure UI reflects server state
            setDraftRefreshTrigger(prev => prev + 1);
        } catch (err) {
            setError('Failed to publish draft: ' + err.message);
            throw err;
        } finally {
            setIsLoading(false);
            // Keep animation active for a bit longer to show the effect
            setTimeout(() => setIsSaveAnimationActive(false), 1000);
        }
    };

    // Display loading or error state for initial fetch
    if (!isDraftManagerInitialized || (!hasLoadedDefinitionsOnce && isFetchingDefinitions) || isFetchingPeriods || isFetchingYearStructure || isLoadingAssets) {
        return (
            <div className="flex items-center justify-center h-screen">
                <p className="text-gray-500 text-lg">
                    Loading {
                        !isDraftManagerInitialized ? 'application state' :
                            isLoadingAssets ? 'teachers, groups, subjects, and rooms' :
                                isFetchingPeriods ? 'periods' :
                                    isFetchingYearStructure ? 'year structure' :
                                        'schedule data'
                    }...
                </p>
            </div>
        );
    }

    if (fetchDefinitionsError || fetchPeriodsError || assetsError) {
        // Special handling for missing academic year structure or periods
        if (fetchPeriodsError?.includes('No academic years found') || fetchPeriodsError?.includes('No periods found')) {
            return (
                <div className="flex flex-col items-center justify-center h-screen p-4 space-y-4">
                    <div className="text-center">
                        <p className="text-lg text-gray-700 mb-2">
                            {fetchPeriodsError?.includes('No academic years found')
                                ? "You first need to setup your year structure before you can schedule your classes"
                                : "You need to setup your academic periods before you can schedule your classes"
                            }
                        </p>
                        <Button
                            onClick={() => window.location.href = '/setup/year-structure'}
                            variant="primary"
                            className="px-6 py-2"
                        >
                            Setup Year Structure
                        </Button>
                    </div>
                </div>
            );
        }

        // Default error handling for other errors
        return (
            <div className="flex flex-col items-center justify-center h-screen p-4">
                <p className="text-red-500 text-lg mb-4">
                    Error: {fetchDefinitionsError || fetchPeriodsError || assetsError}
                </p>
                <Button onClick={() => window.location.reload()} variant="primary">
                    Try Reloading
                </Button>
            </div>
        );
    }

    // toggleDebugDrawer provided by useClassSchedulerState

    return (
        <>
            <div className="flex flex-col h-screen bg-gray-100">
                {/* Fixed Header Section */}
                <ClassSchedulerHeader
                    rooms={rooms}
                    isLiveMode={isLiveMode}
                    currentDraftName={currentDraftName}
                    changeLogLength={changeLog.length}
                    openChangeLogModal={openChangeLogModal}
                    isAdmin={isAdmin}
                    selectedYearForView={selectedYearForView}
                    isImporting={isImporting}
                    isExporting={isExporting}
                    importError={importError}
                    importSuccess={importSuccess}
                    isLoading={isLoading}
                    error={error}
                    success={success}
                    setError={setError}
                    setSuccess={setSuccess}
                    setIsLoading={setIsLoading}
                    draftManager={draftManager}
                    onToggleDebug={toggleDebugDrawer}
                    onModeChange={handleModeChange}
                    onDraftChange={handleDraftChange}
                    onSaveCurrent={handleSaveCurrent}
                    onPublishDraft={handlePublishDraft}
                    onFileUpload={handleFileUpload}
                    onExport={handleExport}
                    currentView={currentView}
                    setCurrentView={setCurrentView}
                    timePeriods={timePeriods}
                    selectedPeriodId={selectedPeriodId}
                    onPeriodChange={handlePeriodChange}
                    availableYears={availableYears}
                    onYearChange={handleYearChange}
                    onMonthChange={handleMonthChange}
                    selectedMonth={selectedMonth}
                    onWeekChange={handleWeekChange}
                    selectedWeek={selectedWeek}
                    textFilterQuery={textFilterQuery}
                    setTextFilterQuery={setTextFilterQuery}
                    allRoomsForGrid={allRoomsForGrid}
                    selectedRoomForView={selectedRoomForView}
                    setSelectedRoomForView={setSelectedRoomForView}
                    teachers={teachers}
                    selectedTeacherForView={selectedTeacherForView}
                    setSelectedTeacherForView={setSelectedTeacherForView}
                    entryTypeFilters={entryTypeFilters}
                    setEntryTypeFilters={setEntryTypeFilters}
                />

                {/* Scrollable Content Section */}
                <div className="flex flex-grow min-h-0">
                    {/* Sidebar - Fixed width, scrollable if needed */}
                    {currentView === 'calendar' && rooms.length > 0 && (
                        <div className={`flex-none transition-all duration-300 ease-in-out ${isUnscheduledDrawerOpen || isEventDrawerOpen || isMeetingDrawerOpen ? 'w-80' : 'w-12'} py-4 px-2 relative`}>
                            {/* Toggle Buttons */}
                            <SidebarDrawerToggle
                                isUnscheduledDrawerOpen={isUnscheduledDrawerOpen}
                                setIsUnscheduledDrawerOpen={setIsUnscheduledDrawerOpen}
                                isEventDrawerOpen={isEventDrawerOpen}
                                setIsEventDrawerOpen={setIsEventDrawerOpen}
                                isMeetingDrawerOpen={isMeetingDrawerOpen}
                                setIsMeetingDrawerOpen={setIsMeetingDrawerOpen}
                            />

                            {/* Unscheduled Drawer Content */}
                            <div className={`absolute top-4 left-[53px] right-2 bottom-0 transition-all duration-300 ease-in-out ${isUnscheduledDrawerOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full pointer-events-none'}`}>
                                <UnscheduledEntryContainer
                                    unscheduledCourses={unscheduledCourses}
                                    onDragStartInternal={handleGlobalDragStart}
                                    onRemoveScheduledItem={handleRemoveScheduledItem}
                                    onDeleteUnscheduled={handleDeleteUnscheduled}
                                    draggedItemInfo={draggedItemInfo}
                                />
                            </div>

                            {/* Event Drawer Content */}
                            <div className={`absolute top-4 left-[53px] right-2 bottom-0 transition-all duration-300 ease-in-out ${isEventDrawerOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full pointer-events-none'}`}>
                                <EventContainer
                                    baseScheduleItems={scheduledInstances}
                                    currentDate={currentDate}
                                    isAdmin={isAdmin}
                                    isPublicView={false}
                                    onOpenScheduleModal={handleOpenModal}
                                    groups={groups}
                                    subjects={subjects}
                                    teachers={teachers}
                                    rooms={rooms}
                                    activeDays={activeDays}
                                    panelTitle="Events"
                                    showTitle={true}
                                />
                            </div>

                            {/* Meeting Drawer Content */}
                            <div className={`absolute top-4 left-[53px] right-2 bottom-0 transition-all duration-300 ease-in-out ${isMeetingDrawerOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full pointer-events-none'}`}>
                                <EventContainer
                                    baseScheduleItems={scheduledInstances}
                                    currentDate={currentDate}
                                    isAdmin={isAdmin}
                                    isPublicView={false}
                                    onOpenScheduleModal={handleOpenModal}
                                    groups={groups}
                                    subjects={subjects}
                                    teachers={teachers}
                                    rooms={rooms}
                                    activeDays={activeDays}
                                    entryType="meeting"
                                    panelTitle="Meetings"
                                    emptyMessage="No upcoming meetings."
                                    addButtonLabel="Add New Meeting"
                                    documentationId="meetings"
                                    showTitle={true}
                                />
                            </div>
                        </div>
                    )}

                    <div ref={scrollContainerRef} className="flex-grow py-4 overflow-y-auto">
                        {(hasLoadedDefinitionsOnce || !isFetchingDefinitions) && !isFetchingPeriods && !isFetchingYearStructure && !isLoadingAssets ? (
                            rooms.length === 0 ? (
                                // Show message when no rooms are available
                                <div className="flex flex-col items-center justify-center h-full p-4 space-y-4">
                                    <div className="text-center">
                                        <p className="text-lg text-gray-700 mb-2">
                                            You need to setup your rooms before you can schedule your classes
                                        </p>
                                        <Button
                                            onClick={() => window.location.href = '/setup/rooms'}
                                            variant="primary"
                                            className="px-6 py-2"
                                        >
                                            Setup Rooms
                                        </Button>
                                    </div>
                                </div>
                            ) : currentView === 'calendar' ? (
                                /* Calendar View - Scrollable */
                                <SharedScrollProvider>
                                    <div className="space-y-6">
                                        {activeDays.map(day => {
                                            const itemsForDay = entryTypeFilteredScheduledInstances.filter(item => item.day === day && item.isScheduled());
                                            return (
                                                <div key={`${day}-${selectedPeriodId || 'whole-year'}`}>
                                                    <DayTableSimple
                                                        key={`${day}-${selectedPeriodId || 'whole-year'}`}
                                                        day={day}
                                                        rooms={allRoomsForGrid}
                                                        items={itemsForDay}
                                                        dayScheduleEntry={entryTypeFilteredScheduledInstances}
                                                        user={user}
                                                        onOpenModal={handleOpenModal}
                                                        onItemDrop={handleActualItemDrop}
                                                        onItemResize={handleItemResize}
                                                        draggedItemInfo={draggedItemInfo}
                                                        onGlobalDragStart={handleGlobalDragStart}
                                                        onGlobalDragEnd={handleGlobalDragEnd}
                                                        availabilityOverlayData={availabilityOverlayData}
                                                        KEY_SEPARATOR={KEY_SEPARATOR}
                                                        teachers={teachers}
                                                        groups={groups}
                                                        subjects={subjects}
                                                        currentWeekNumber={selectedWeek?.week}
                                                        currentYear={selectedWeek?.year || selectedYearForView}
                                                        dragToCreateData={dragToCreateData}
                                                        onDragToCreateStart={handleDragToCreateStart}
                                                        onDragToCreateMove={handleDragToCreateMove}
                                                        onDragToCreateEnd={handleDragToCreateEnd}
                                                        onDragToCreateCancel={handleDragToCreateCancel}
                                                        onDuplicateItem={handleDuplicateItem}
                                                        onDelete={handleDeleteFromGrid}
                                                        dayHeaderRef={element => dayHeaderRefs.current[day] = element}
                                                        showMondayHeader={day === 'monday'}
                                                        isSaveAnimationActive={isSaveAnimationActive}
                                                        highlightedItemId={highlightedItemId}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </SharedScrollProvider>
                            ) : currentView === 'room' ? (
                                /* Rooms Occupancy View */
                                <RoomsOccupancyView
                                    rooms={rooms}
                                    scheduleEntries={entryTypeFilteredScheduledInstances.filter(item => item.isScheduled())}
                                    selectedPeriod={selectedPeriodForRoomView}
                                    activeDays={activeDays}
                                    onTimeSlotClick={(entry) => handleOpenModal(entry)}
                                />
                            ) : (
                                /* Teacher View */
                                <TeacherView
                                    teacher={selectedTeacherForView}
                                    scheduledInstances={entryTypeFilteredScheduledInstances.filter(item => item.isScheduled())}
                                    startWeek={36}
                                    year={selectedYearForView}
                                    user={user}
                                    onOpenModal={handleOpenModal}
                                    timelineViewRange={timelineViewRange}
                                />
                            )
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <p className="text-gray-500">Loading schedule data...</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* EditScheduleItemModal */}
                <EditScheduleItemModal
                    isOpen={isScheduleModalOpen}
                    onClose={closeScheduleModal}
                    selectedScheduleItem={selectedScheduleItem}
                    onSaveEdit={handleSaveEdit}
                    onSaveEditImmediate={handleSaveEdit}
                    onDelete={handleDeleteFromModal}
                    groups={groups}
                    subjects={subjects}
                    teachers={teachers}
                    rooms={rooms}
                    // Academic-year start year (used for exception IDs & week->year mapping)
                    currentYear={selectedYearForView}
                    currentDate={new Date()}
                    isAdmin={isAdmin}
                    baseScheduleItems={scheduledInstances}
                    onAddTeacher={handleAddTeacher}
                    onAddSubject={handleAddSubject}
                    onAddGroup={handleAddGroup}
                    currentTimeRangeContext={{
                        selectedPeriodId,
                        selectedMonth,
                        selectedWeek,
                        timePeriods
                    }}
                    draftMode={!isLiveMode}
                />

                {/* ChangeLogModal moved into FloatingActionButtons */}

                {/* ConflictListModal */}
                <ConflictListModal
                    isOpen={isConflictListVisible}
                    onClose={toggleConflictList}
                    conflicts={currentConflicts}
                    currentWeekNumber={getWeekNumber(new Date())}
                    currentYear={new Date().getFullYear()}
                />

                {/* Week Change Confirmation Modal - Not shown in ClassSchedulerPage due to autoApplyToAllWeeks: true */}

                {/* Floating Action Buttons */}
                <FloatingActionButtons
                    showSaveDraft={!isLiveMode && currentDraftName}
                    showPublishDraft={!isLiveMode && currentDraftName}
                    showConflictIndicator={isAdmin}
                    showReviewChanges={isLiveMode && isAdmin}
                    isLoading={isLoading}
                    changeLogLength={changeLog.length}
                    changeLog={changeLog}
                    currentDraftName={currentDraftName}
                    isLiveMode={isLiveMode}
                    currentConflicts={currentConflicts}
                    scheduledInstances={scheduledInstances}
                    serverBaseline={serverBaseline}
                    // New: ID-set change counts for save enablement
                    changeIdCounts={typeof getChangeIdCounts === 'function' ? getChangeIdCounts() : { created: 0, modified: 0, deleted: 0 }}
                    hasPendingChangesByIdSets={typeof hasPendingChangesByIdSets === 'function' ? hasPendingChangesByIdSets() : false}
                    // Undo/Redo functionality
                    canUndo={canUndo()}
                    canRedo={canRedo()}
                    onUndo={handleUndo}
                    onRedo={handleRedo}
                    onSaveCurrent={handleSaveCurrent}
                    onPublishDraft={handlePublishDraft}
                    onToggleConflictList={() => setIsConflictListVisible(prev => !prev)}
                    // Data for PublishDraftModal
                    timePeriods={timePeriods}
                    selectedYearForView={selectedYearForView}
                    institutionUrlEndpoint={assetManager?.getInstitutionUrlEndpoint && assetManager.getInstitutionUrlEndpoint()}
                    onSaveChangeLog={() => {
                        console.log('*** CHANGELOGMODAL ONSAVE CALLBACK CALLED ***');
                        try {
                            logger.debug('[ClassSchedulerPage] ChangeLogModal onSave called', {
                                changeLogLength: changeLog.length,
                                scheduledInstancesLength: scheduledInstances.length,
                                isLiveMode,
                                currentDraftName
                            });

                            const maybeBackup = async () => {
                                if (isAdmin && isLiveMode) {
                                    try {
                                        await LiveBackupManager.backupCurrentLive(assetManager, draftManager, selectedYearForView, 20);
                                    } catch (e) {
                                        console.warn('[ClassSchedulerPage] Live backup before ChangeLog save failed (non-fatal):', e);
                                    }
                                }
                            };

                            const result = (async () => {
                                await maybeBackup();
                                return handleSaveChanges(scheduledInstances, saveChangesToAPI, () => {
                                    setDraftRefreshTrigger(prev => prev + 1);
                                    setIsBaselineSet(false);
                                    setHasInitialConflictsCalculated(false);
                                });
                            })();
                            return result; // Promise
                        } catch (error) {
                            console.error('*** CHANGELOGMODAL ONSAVE CALLBACK ERROR ***', error);
                            throw error;
                        }
                    }}
                />

                {/* Debug Drawer */}
                <DebugDrawer
                    isOpen={isDebugDrawerOpen}
                    onClose={() => setIsDebugDrawerOpen(false)}
                    courseDefinitions={courseDefinitions}
                    scheduledInstances={scheduledInstances}
                    selectedPeriodId={selectedPeriodId}
                    timePeriods={timePeriods}
                    selectedYearForView={selectedYearForView}
                />

                {/* Success/Error Messages */}
                {error && (
                    <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
                        <span className="block sm:inline">{error}</span>
                        <button
                            className="float-right ml-4 text-red-500 hover:text-red-700"
                            onClick={() => setError(null)}
                        >
                            ×
                        </button>
                    </div>
                )}

                {(success || importSuccess) && (
                    <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50 max-w-md">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                {success && (
                                    <span className="block sm:inline">
                                        {typeof success === 'string' ? success : success.message}
                                    </span>
                                )}
                                {importSuccess && (
                                    <div>
                                        Import completed: {importSuccess.successful} successful, {importSuccess.failed} failed
                                        {importSuccess.draftName && (
                                            <div className="text-xs mt-1 text-blue-600">
                                                Created draft: "{importSuccess.draftName}"
                                            </div>
                                        )}
                                        {importSuccess.details && (
                                            <div className="text-xs mt-1">
                                                {/* Show first few failures if any */}
                                                {importSuccess.details.failures && importSuccess.details.failures.length > 0 && (
                                                    <div className="text-red-600">
                                                        Failures: {importSuccess.details.failures.slice(0, 2).map((failure, idx) => (
                                                            <div key={idx}>• {failure.reason}</div>
                                                        ))}
                                                        {importSuccess.details.failures.length > 2 &&
                                                            <div>... and {importSuccess.details.failures.length - 2} more failures</div>
                                                        }
                                                    </div>
                                                )}
                                                {/* Show some success details */}
                                                {importSuccess.details.successes && importSuccess.details.successes.length > 0 && (
                                                    <div className="text-green-600">
                                                        Successfully imported {importSuccess.details.successes.length} entries
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {typeof success === 'object' && success.action && (
                                    <div className="mt-2">
                                        <button
                                            className="text-green-600 hover:text-green-800 underline font-medium text-sm"
                                            onClick={success.action.onClick}
                                        >
                                            {success.action.label}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button
                                className="ml-4 text-green-500 hover:text-green-700 flex-shrink-0"
                                onClick={() => {
                                    setSuccess(null);
                                    setImportSuccess(null);
                                }}
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default ClassSchedulerPage;

